'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import type { LineElementOwnerState } from './LineElement';
import { useAnimateLine } from '../hooks';
import { AppearingMask } from './AppearingMask';

export interface AnimatedLineProps extends React.ComponentPropsWithoutRef<'path'> {
  /**
   * @ignore
   */
  ownerState: LineElementOwnerState;
  /**
   * The SVG path of the element.
   */
  d: string;
  /**
   * If `true`, animations are skipped.
   * @default false
   */
  skipAnimation?: boolean;
}

/**
 * Demos:
 *
 * - [Lines](https://mui.com/x/react-charts/lines/)
 * - [Line demonstration](https://mui.com/x/react-charts/line-demo/)
 *
 * API:
 *
 * - [AnimatedLine API](https://mui.com/x/api/charts/animated-line/)
 */
const AnimatedLine = React.forwardRef<SVGPathElement, AnimatedLineProps>(
  function AnimatedLine(props, ref) {
    const { skipAnimation, ownerState, ...other } = props;

    const animateProps = useAnimateLine({ d: props.d, skipAnimation, ref });
    const fadedOpacity = ownerState.isFaded ? 0.3 : 1;

    return (
      <AppearingMask skipAnimation={skipAnimation} seriesId={`${ownerState.seriesId}-line-clip`}>
        <path
          stroke={ownerState.gradientId ? `url(#${ownerState.gradientId})` : ownerState.color}
          strokeWidth={2}
          strokeLinejoin="round"
          fill="none"
          filter={ownerState.isHighlighted ? 'brightness(120%)' : undefined}
          opacity={ownerState.hidden ? 0 : fadedOpacity}
          data-series={ownerState.seriesId}
          data-highlighted={ownerState.isHighlighted || undefined}
          data-faded={ownerState.isFaded || undefined}
          {...other}
          {...animateProps}
        />
      </AppearingMask>
    );
  },
);

AnimatedLine.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The SVG path of the element.
   */
  d: PropTypes.string.isRequired,
  /**
   * @ignore
   */
  ownerState: PropTypes.shape({
    classes: PropTypes.object,
    color: PropTypes.string.isRequired,
    gradientId: PropTypes.string,
    hidden: PropTypes.bool,
    isFaded: PropTypes.bool.isRequired,
    isHighlighted: PropTypes.bool.isRequired,
    seriesId: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * If `true`, animations are skipped.
   * @default false
   */
  skipAnimation: PropTypes.bool,
} as any;

export { AnimatedLine };
